import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { FilterOption, ProductsToPickup } from 'pages/picking-management/types';
import { Loading, Text } from 'components';
import { getLocalStorageItem, setLocalStorageItem } from 'helpers';
import { FontSize, FontWeight, LocalStorageKeys } from 'commons';
import * as S from './styles';
import { ProductCard } from '../product-card';

interface Props {
  filter: string;
  isLoading: boolean;
  productsToPickup: ProductsToPickup[];
}

export function ProductList({ filter, isLoading, productsToPickup }: Props) {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  const filteredProductsByHour = useMemo(
    () =>
      productsToPickup.reduce(
        (acc, item) => {
          const hours = item.availableAt.getHours();

          const productHour = Number(hours);
          const currAcc = acc[productHour] || [];
          currAcc.push(item);
          acc[productHour] = currAcc;
          return acc;
        },
        {} as { [key: number]: ProductsToPickup[] }
      ),
    [productsToPickup]
  );

  useEffect(() => {
    const checkedProducts = getLocalStorageItem(
      LocalStorageKeys.CheckedPickupProductsList,
      []
    );

    if (checkedProducts) {
      setSelectedProducts(checkedProducts);
    }
  }, []);

  useEffect(() => {
    if (selectedProducts) {
      setLocalStorageItem(
        LocalStorageKeys.CheckedPickupProductsList,
        selectedProducts
      );
    }
  }, [selectedProducts]);

  function handleCheckboxSelection(e: ChangeEvent<HTMLInputElement>) {
    const { checked, name } = e.currentTarget;

    if (checked) {
      setSelectedProducts((state) => [...state, name]);
    } else {
      setSelectedProducts((state) => state.filter((item) => item !== name));
    }
  }

  function sortAndMapProducts(products: ProductsToPickup[]) {
    return products
      .sort((a, b) =>
        a.legacySKU
          .toLocaleLowerCase()
          .localeCompare(b.legacySKU.toLocaleLowerCase())
      )
      .map((product, index) => {
        const isProductChecked = selectedProducts.some(
          (prod) => prod === `${product.consignmentCode}/${product.legacySKU}`
        );
        return (
          <ProductCard
            key={product.legacySKU + index}
            product={product}
            isChecked={isProductChecked}
            handleChange={handleCheckboxSelection}
          />
        );
      });
  }

  function renderProductsByHour() {
    return (
      <S.HourWrapper>
        {Object.entries(filteredProductsByHour)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([hour, products]) => (
            <S.Hour key={hour}>
              <Text size={FontSize.font16} weight={FontWeight.semiBold}>
                Disponível às {hour} horas
              </Text>
              <S.ProductListWrapper>
                {sortAndMapProducts(products)}
              </S.ProductListWrapper>
            </S.Hour>
          ))}
      </S.HourWrapper>
    );
  }

  function renderProductsToPickup() {
    if (isLoading) {
      return (
        <S.ErrorAndLoaderWrapper>
          <Loading />
        </S.ErrorAndLoaderWrapper>
      );
    }

    if (Boolean(!productsToPickup.length)) {
      return (
        <S.ErrorAndLoaderWrapper>
          <Text>Não há produtos disponíveis para entrega.</Text>
        </S.ErrorAndLoaderWrapper>
      );
    }

    if (filter === FilterOption.hour) {
      return renderProductsByHour();
    } else {
      return sortAndMapProducts(productsToPickup);
    }
  }

  return (
    <S.ProductListWrapper>{renderProductsToPickup()}</S.ProductListWrapper>
  );
}
